import React, { useState, useEffect, useCallback } from "react";
import { budgetPlannerAPI, exportAPI, installmentSchedulesAPI, liabilityAccountsAPI, savingsAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { getPaymentStatusColor, getDueDateUrgencyColor, PAYMENT_STATUS_LABELS } from "../lib/utils";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Plus,
  Trash2,
  Download,
  Lock,
  Save,
  AlertCircle,
  FileText,
  Info,
  ShoppingCart,
  CheckCircle2,
  AlertTriangle,
  CreditCard,
  Pencil,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);

const PAYMENT_STATUSES = [
  { value: "planned", label: "Planned" },
  { value: "pending", label: "Pending" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

const PAYMENT_TYPES = [
  { value: "one_time", label: "One-Time" },
  { value: "recurring", label: "Recurring" },
  { value: "installment", label: "Installment" },
];

const PRIORITIES = [
  { value: "critical", label: "Critical" },
  { value: "important", label: "Important" },
  { value: "normal", label: "Normal" },
  { value: "flexible", label: "Flexible" },
];

const formatCurrency = (amount, currency = "PHP") => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount || 0);
  } catch {
    return `${currency} ${(amount || 0).toFixed(2)}`;
  }
};

const formatMonthYear = (month, year) => {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const emptyLine = () => ({
  _key: Math.random().toString(36).slice(2),
  category: "",
  subcategory: "",
  description: "",
  allocated_budget: "",
  actual_expenditure: "",
  row_state: "planned",
  due_date: "",
  payment_status: "planned",
  payment_type: "one_time",
  priority: "normal",
  payment_source_type: "",
  payment_source_account_id: "",
  actual_paid_date: "",
  actual_payment_notes: "",
});

// Compute display label for a due date relative to today
const getDueDateLabel = (due_date, payment_status) => {
  if (!due_date) return null;
  if (payment_status === "paid") return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(due_date + "T00:00:00");
  const delta = Math.round((due - today) / 86400000);
  if (delta < 0) return { text: "Overdue", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" };
  if (delta === 0) return { text: "Due Today", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" };
  if (delta <= 3) return { text: `Due in ${delta}d`, color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300" };
  if (delta <= 7) return { text: "Due This Week", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" };
  return null;
};

// ─── Row State helpers ─────────────────────────────────────────────────────────

const ROW_STATE_LABELS = {
  planned: "Planned",
  actual_recorded: "Actual Recorded",
  queued_for_expense_tracker: "Queued for ET",
  synced_to_expense_tracker: "Synced to ET",
};

const ROW_STATE_COLORS = {
  planned: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  actual_recorded: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  queued_for_expense_tracker: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  synced_to_expense_tracker: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
};

const RowStateBadge = ({ state }) => {
  const label = ROW_STATE_LABELS[state] || state;
  const color = ROW_STATE_COLORS[state] || ROW_STATE_COLORS.planned;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
      {label}
    </span>
  );
};

const PaymentStatusBadge = ({ status }) => {
  const s = status || "planned";
  const label = PAYMENT_STATUS_LABELS[s] || s.replace(/_/g, " ");
  const color = getPaymentStatusColor(s);
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
      {label}
    </span>
  );
};

// ─── Budget Form Tab ───────────────────────────────────────────────────────────

const BudgetFormTab = ({ onPlanLocked }) => {
  const { user, isAdmin } = useAuth();
  const defaultCurrency = user?.default_currency || "PHP";

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [plan, setPlan] = useState(null);
  const [lines, setLines] = useState([emptyLine()]);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categories, setCategories] = useState({ builtin: {}, custom: [] });
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ category: "", subcategories: "" });
  const [assetAccounts, setAssetAccounts] = useState([]);
  const [liabilityAccounts, setLiabilityAccounts] = useState([]);

  // Load categories once
  const loadCategories = useCallback(async () => {
    try {
      const res = await budgetPlannerAPI.getCategories();
      setCategories(res.data);
    } catch {
      // silently fail; built-ins will still be available via server
    }
  }, []);

  // Load asset and liability accounts once
  const loadAccounts = useCallback(async () => {
    try {
      const [assetRes, liabilityRes] = await Promise.all([
        savingsAPI.list(),
        liabilityAccountsAPI.list(),
      ]);
      setAssetAccounts((assetRes.data || []).filter((a) => !a.deleted_at));
      setLiabilityAccounts((liabilityRes.data || []).filter((a) => !a.deleted_at && a.is_active !== false));
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    loadCategories();
    loadAccounts();
  }, [loadCategories, loadAccounts]);

  // Merge built-in + custom categories into a flat map
  const allCategories = React.useMemo(() => {
    const map = { ...categories.builtin };
    (categories.custom || []).forEach((c) => {
      map[c.category] = c.subcategories || [];
    });
    return map;
  }, [categories]);

  const categoryNames = Object.keys(allCategories);

  // Load plan whenever month/year changes
  const loadPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await budgetPlannerAPI.getPlanByMonth(selectedMonth, selectedYear);
      const existing = res.data;
      if (existing) {
        setPlan(existing);
        setCurrency(existing.currency || defaultCurrency);
        if (existing.lines && existing.lines.length > 0) {
          setLines(
            existing.lines.map((l) => ({
              ...l,
              _key: l.id || Math.random().toString(36).slice(2),
              allocated_budget: l.allocated_budget ?? "",
              actual_expenditure: l.actual_expenditure ?? "",
              row_state: l.row_state || "planned",
              due_date: l.due_date || "",
              payment_status: l.payment_status || "planned",
              payment_type: l.payment_type || "one_time",
              priority: l.priority || "normal",
              payment_source_type: l.payment_source_type || "",
              payment_source_account_id: l.payment_source_account_id || "",
              actual_paid_date: l.actual_paid_date || "",
              actual_payment_notes: l.actual_payment_notes || "",
            }))
          );
        } else {
          setLines([emptyLine()]);
        }
      } else {
        // No plan for this month-year yet
        setPlan(null);
        setCurrency(defaultCurrency);
        setLines([emptyLine()]);
      }
    } catch {
      toast.error("Failed to load budget plan");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, defaultCurrency]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const isLocked = plan?.status === "locked";

  // ── Line helpers ──────────────────────────────────────────────────────────

  const updateLine = (key, field, value) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l._key !== key) return l;
        const updated = { ...l, [field]: value };
        // Reset subcategory if category changes
        if (field === "category") updated.subcategory = "";
        // Auto-transition row_state for actual_expenditure changes
        // (only when not already queued/synced)
        if (field === "actual_expenditure") {
          const hasActual = parseFloat(value) > 0;
          if (l.row_state === "planned" && hasActual) {
            updated.row_state = "actual_recorded";
          } else if (l.row_state === "actual_recorded" && !hasActual) {
            updated.row_state = "planned";
          }
          // Clear payment source when actual drops to 0
          if (!hasActual) {
            updated.payment_source_type = "";
            updated.payment_source_account_id = "";
            updated.actual_paid_date = "";
            updated.actual_payment_notes = "";
          }
        }
        // Reset account selection when payment source type changes
        if (field === "payment_source_type") {
          updated.payment_source_account_id = "";
        }
        return updated;
      })
    );
  };

  const toggleQueueLine = (key) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l._key !== key) return l;
        if (l.row_state === "queued_for_expense_tracker") {
          // Dequeue: revert to actual_recorded or planned
          const actual = parseFloat(l.actual_expenditure) || 0;
          return { ...l, row_state: actual > 0 ? "actual_recorded" : "planned" };
        }
        // Queue it (only from planned or actual_recorded)
        if (l.row_state === "planned" || l.row_state === "actual_recorded") {
          return { ...l, row_state: "queued_for_expense_tracker" };
        }
        return l;
      })
    );
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (key) => {
    setLines((prev) => {
      const next = prev.filter((l) => l._key !== key);
      return next.length === 0 ? [emptyLine()] : next;
    });
  };

  // ── Build payload ─────────────────────────────────────────────────────────

  const buildLines = () =>
    lines
      .filter((l) => l.category.trim() !== "")
      .map((l) => ({
        id: l.id || undefined,
        category: l.category,
        subcategory: l.subcategory || "",
        description: l.description || "",
        allocated_budget: parseFloat(l.allocated_budget) || 0,
        actual_expenditure: parseFloat(l.actual_expenditure) || 0,
        row_state: l.row_state || "planned",
        due_date: l.due_date || null,
        payment_status: l.payment_status || "planned",
        payment_type: l.payment_type || "one_time",
        priority: l.priority || "normal",
        payment_source_type: l.payment_source_type || null,
        payment_source_account_id: l.payment_source_account_id || null,
        actual_paid_date: l.actual_paid_date || null,
        actual_payment_notes: l.actual_payment_notes || null,
      }));

  // ── Save Draft ────────────────────────────────────────────────────────────

  const handleSaveDraft = async () => {
    if (isLocked) return;
    setSaving(true);
    try {
      const linesPayload = buildLines();
      if (plan) {
        // Update existing draft
        const res = await budgetPlannerAPI.updatePlan(plan.id, {
          lines: linesPayload,
          currency,
        });
        setPlan(res.data);
        toast.success("Draft saved");
      } else {
        // Create new draft
        const res = await budgetPlannerAPI.createPlan({
          month: selectedMonth,
          year: selectedYear,
          currency,
          lines: linesPayload,
        });
        setPlan(res.data);
        toast.success("Draft saved");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  // ── Save & Lock ───────────────────────────────────────────────────────────

  const handleSaveLock = async () => {
    if (isLocked) return;

    // Client-side validation: lines with actual > 0 must have a payment source
    const missingSource = lines.find(
      (l) =>
        l.category.trim() !== "" &&
        parseFloat(l.actual_expenditure) > 0 &&
        !l.payment_source_account_id
    );
    if (missingSource) {
      const label =
        missingSource.description ||
        missingSource.subcategory ||
        missingSource.category ||
        "a budget line";
      toast.error(`Payment source required for "${label}" (Actual > 0).`);
      return;
    }

    setLocking(true);
    try {
      const linesPayload = buildLines();
      let targetPlan = plan;

      if (!targetPlan) {
        // Create first, then lock
        const res = await budgetPlannerAPI.createPlan({
          month: selectedMonth,
          year: selectedYear,
          currency,
          lines: linesPayload,
        });
        targetPlan = res.data;
      } else {
        // Save lines first
        const res = await budgetPlannerAPI.updatePlan(targetPlan.id, {
          lines: linesPayload,
          currency,
        });
        targetPlan = res.data;
      }

      await budgetPlannerAPI.lockPlan(targetPlan.id);
      toast.success("Budget plan locked and sent to History");

      // Reset form for next month's planning
      const nextMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
      const nextYear = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
      setSelectedMonth(nextMonth);
      setSelectedYear(nextYear);

      if (onPlanLocked) onPlanLocked();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to lock budget plan");
    } finally {
      setLocking(false);
    }
  };

  // ── Admin delete ──────────────────────────────────────────────────────────

  const handleAdminDelete = async () => {
    if (!plan) return;
    setDeleting(true);
    try {
      await budgetPlannerAPI.adminDeletePlan(plan.id);
      toast.success("Budget plan deleted");
      setShowDeleteConfirm(false);
      setPlan(null);
      setLines([emptyLine()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete budget plan");
    } finally {
      setDeleting(false);
    }
  };

  // ── Add custom category ────────────────────────────────────────────────────

  const handleAddCategory = async () => {
    const catName = newCategory.category.trim();
    if (!catName) {
      toast.error("Category name is required");
      return;
    }
    try {
      const subs = newCategory.subcategories
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await budgetPlannerAPI.createCategory({ category: catName, subcategories: subs });
      toast.success("Category added");
      setShowCategoryDialog(false);
      setNewCategory({ category: "", subcategories: "" });
      loadCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add category");
    }
  };

  // ── Totals ────────────────────────────────────────────────────────────────

  const totalAllocated = lines.reduce(
    (sum, l) => sum + (parseFloat(l.allocated_budget) || 0),
    0
  );
  const totalActual = lines.reduce(
    (sum, l) => sum + (parseFloat(l.actual_expenditure) || 0),
    0
  );
  const totalDiff = totalAllocated - totalActual;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Month</Label>
              <Select
                value={String(selectedMonth)}
                onValueChange={(v) => setSelectedMonth(Number(v))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={String(m.value)}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Year</Label>
              <Select
                value={String(selectedYear)}
                onValueChange={(v) => setSelectedYear(Number(v))}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Currency</Label>
              <Input
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                className="w-24"
                maxLength={3}
                disabled={isLocked}
              />
            </div>

            {plan && (
              <Badge
                variant={isLocked ? "destructive" : "secondary"}
                className="mb-0.5"
              >
                {isLocked ? (
                  <>
                    <Lock className="h-3 w-3 mr-1" /> Locked
                  </>
                ) : (
                  "Draft"
                )}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Locked notice */}
      {isLocked && (
        <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
          <Lock className="h-4 w-4 flex-shrink-0" />
          This budget plan is locked. To plan a new budget, select a different month/year.
        </div>
      )}

      {/* Analytics status banner */}
      {plan && (
        <div
          className={
            isLocked
              ? "flex items-center gap-2 rounded-md border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400"
              : "flex items-center gap-2 rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-700 dark:text-blue-400"
          }
        >
          {isLocked ? (
            <>
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span>
                <strong>Locked / Final Status</strong> — Analytics below reflect the
                final actual figures for this month.
              </span>
            </>
          ) : (
            <>
              <Info className="h-4 w-4 flex-shrink-0" />
              <span>
                <strong>Draft / Projected Status</strong> — Analytics below are
                projected. Queue rows for Expense Tracker and click &ldquo;Save &amp; Lock&rdquo; to
                finalize.
              </span>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Budget lines table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">
                  Budget Lines — {formatMonthYear(selectedMonth, selectedYear)}
                </CardTitle>
                {!isLocked && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCategoryDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Category
                    </Button>
                    <Button variant="outline" size="sm" onClick={addLine}>
                      <Plus className="h-4 w-4 mr-1" /> Add Line
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[130px]">Category</TableHead>
                      <TableHead className="min-w-[150px]">Subcategory</TableHead>
                      <TableHead className="min-w-[180px]">Description</TableHead>
                      <TableHead className="min-w-[130px] text-right">Allocated</TableHead>
                      <TableHead className="min-w-[130px] text-right">Actual</TableHead>
                      <TableHead className="min-w-[200px]">Payment Source</TableHead>
                      <TableHead className="min-w-[130px] text-right">Difference</TableHead>
                      <TableHead className="min-w-[130px]">Status</TableHead>
                      <TableHead className="min-w-[140px]">Due Date</TableHead>
                      <TableHead className="min-w-[140px]">Payment Status</TableHead>
                      <TableHead className="min-w-[130px]">Payment Type</TableHead>
                      <TableHead className="min-w-[120px]">Priority</TableHead>
                      {!isLocked && <TableHead className="w-20" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line) => {
                      const allocated = parseFloat(line.allocated_budget) || 0;
                      const actual = parseFloat(line.actual_expenditure) || 0;
                      const diff = allocated - actual;
                      const subcatOptions = allCategories[line.category] || [];
                      const isQueued = line.row_state === "queued_for_expense_tracker";
                      const isSynced = line.row_state === "synced_to_expense_tracker";
                      const canQueue =
                        !isLocked &&
                        line.category.trim() !== "" &&
                        !isSynced;

                      return (
                        <TableRow key={line._key}>
                          {/* Category */}
                          <TableCell>
                            {isLocked ? (
                              <span>{line.category}</span>
                            ) : (
                              <Select
                                value={line.category}
                                onValueChange={(v) =>
                                  updateLine(line._key, "category", v)
                                }
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue placeholder="Select…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {categoryNames.map((cat) => (
                                    <SelectItem key={cat} value={cat}>
                                      {cat}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>

                          {/* Subcategory */}
                          <TableCell>
                            {isLocked ? (
                              <span>{line.subcategory}</span>
                            ) : subcatOptions.length > 0 ? (
                              <Select
                                value={line.subcategory || ""}
                                onValueChange={(v) =>
                                  updateLine(line._key, "subcategory", v)
                                }
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue placeholder="Select…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {subcatOptions.map((sub) => (
                                    <SelectItem key={sub} value={sub}>
                                      {sub}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                className="h-8 text-sm"
                                placeholder="Subcategory"
                                value={line.subcategory || ""}
                                onChange={(e) =>
                                  updateLine(line._key, "subcategory", e.target.value)
                                }
                              />
                            )}
                          </TableCell>

                          {/* Description */}
                          <TableCell>
                            {isLocked ? (
                              <span className="text-sm text-muted-foreground">
                                {line.description}
                              </span>
                            ) : (
                              <Input
                                className="h-8 text-sm"
                                placeholder="Description"
                                value={line.description || ""}
                                onChange={(e) =>
                                  updateLine(line._key, "description", e.target.value)
                                }
                              />
                            )}
                          </TableCell>

                          {/* Allocated */}
                          <TableCell className="text-right">
                            {isLocked ? (
                              <span className="text-blue-600 dark:text-blue-400 font-medium">{formatCurrency(allocated, currency)}</span>
                            ) : (
                              <Input
                                className="h-8 text-sm text-right text-blue-600 dark:text-blue-400"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={line.allocated_budget}
                                onChange={(e) =>
                                  updateLine(line._key, "allocated_budget", e.target.value)
                                }
                              />
                            )}
                          </TableCell>

                          {/* Actual */}
                          <TableCell className="text-right">
                            {isLocked ? (
                              <span className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(actual, currency)}</span>
                            ) : (
                              <Input
                                className="h-8 text-sm text-right text-green-600 dark:text-green-400"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={line.actual_expenditure}
                                onChange={(e) =>
                                  updateLine(
                                    line._key,
                                    "actual_expenditure",
                                    e.target.value
                                  )
                                }
                              />
                            )}
                          </TableCell>

                          {/* Payment Source */}
                          <TableCell>
                            {actual > 0 ? (
                              isLocked ? (
                                <div className="text-sm space-y-0.5">
                                  {line.payment_source_type === "asset_account" ? (
                                    <span className="text-blue-600 dark:text-blue-400">
                                      {assetAccounts.find((a) => a.id === line.payment_source_account_id)?.account_label ||
                                        assetAccounts.find((a) => a.id === line.payment_source_account_id)?.bank_name ||
                                        line.payment_source_account_id ||
                                        "—"}
                                    </span>
                                  ) : line.payment_source_type === "liability_account" ? (
                                    <span className="text-orange-600 dark:text-orange-400">
                                      {liabilityAccounts.find((a) => a.id === line.payment_source_account_id)?.name ||
                                        line.payment_source_account_id ||
                                        "—"}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                  {line.actual_paid_date && (
                                    <p className="text-xs text-muted-foreground">{line.actual_paid_date}</p>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-1 min-w-[180px]">
                                  {/* Type selector */}
                                  <Select
                                    value={line.payment_source_type || ""}
                                    onValueChange={(v) => updateLine(line._key, "payment_source_type", v)}
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Source type…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="asset_account">Bank / Cash / E-wallet</SelectItem>
                                      <SelectItem value="liability_account">Credit / Loan / PayLater</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {/* Account selector */}
                                  {line.payment_source_type === "asset_account" && (
                                    <Select
                                      value={line.payment_source_account_id || ""}
                                      onValueChange={(v) => updateLine(line._key, "payment_source_account_id", v)}
                                    >
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Select account…" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {assetAccounts.length === 0 ? (
                                          <SelectItem value="" disabled>No accounts found</SelectItem>
                                        ) : (
                                          assetAccounts.map((a) => (
                                            <SelectItem key={a.id} value={a.id}>
                                              {a.account_label || a.bank_name}
                                            </SelectItem>
                                          ))
                                        )}
                                      </SelectContent>
                                    </Select>
                                  )}
                                  {line.payment_source_type === "liability_account" && (
                                    <Select
                                      value={line.payment_source_account_id || ""}
                                      onValueChange={(v) => updateLine(line._key, "payment_source_account_id", v)}
                                    >
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Select account…" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {liabilityAccounts.length === 0 ? (
                                          <SelectItem value="" disabled>No accounts found</SelectItem>
                                        ) : (
                                          liabilityAccounts.map((a) => (
                                            <SelectItem key={a.id} value={a.id}>
                                              {a.name}
                                            </SelectItem>
                                          ))
                                        )}
                                      </SelectContent>
                                    </Select>
                                  )}
                                  {/* Optional paid date */}
                                  {line.payment_source_account_id && (
                                    <Input
                                      type="date"
                                      className="h-7 text-xs"
                                      placeholder="Paid date (optional)"
                                      value={line.actual_paid_date || ""}
                                      onChange={(e) => updateLine(line._key, "actual_paid_date", e.target.value)}
                                    />
                                  )}
                                  {/* Missing source warning */}
                                  {!line.payment_source_account_id && (
                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                      <AlertCircle className="h-3 w-3" /> Required
                                    </p>
                                  )}
                                </div>
                              )
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>

                          {/* Difference */}
                          <TableCell className="text-right">
                            <span
                              className={
                                diff < 0
                                  ? "text-red-600 dark:text-red-400 font-medium"
                                  : "font-medium"
                              }
                            >
                              {formatCurrency(diff, currency)}
                            </span>
                          </TableCell>

                          {/* Status badge */}
                          <TableCell>
                            <RowStateBadge state={line.row_state || "planned"} />
                          </TableCell>

                          {/* Due Date */}
                          <TableCell>
                            {isLocked ? (
                              <div className="space-y-1">
                                <span className={`text-sm ${getDueDateUrgencyColor(line.due_date)}`}>{line.due_date || "—"}</span>
                                {(() => {
                                  const lbl = getDueDateLabel(line.due_date, line.payment_status);
                                  return lbl ? (
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${lbl.color}`}>
                                      {lbl.text}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <Input
                                  className={`h-8 text-sm w-36 ${getDueDateUrgencyColor(line.due_date)}`}
                                  type="date"
                                  value={line.due_date || ""}
                                  onChange={(e) => updateLine(line._key, "due_date", e.target.value)}
                                />
                                {(() => {
                                  const lbl = getDueDateLabel(line.due_date, line.payment_status);
                                  return lbl ? (
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${lbl.color}`}>
                                      {lbl.text}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            )}
                          </TableCell>

                          {/* Payment Status */}
                          <TableCell>
                            {isLocked ? (
                              <PaymentStatusBadge status={line.payment_status} />
                            ) : (
                              <div className="space-y-1">
                                <Select
                                  value={line.payment_status || "planned"}
                                  onValueChange={(v) => updateLine(line._key, "payment_status", v)}
                                >
                                  <SelectTrigger className="h-8 text-sm w-36">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {PAYMENT_STATUSES.map((s) => (
                                      <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <PaymentStatusBadge status={line.payment_status} />
                              </div>
                            )}
                          </TableCell>

                          {/* Payment Type */}
                          <TableCell>
                            {isLocked ? (
                              <span className="text-sm capitalize">
                                {(line.payment_type || "one_time").replace(/_/g, " ")}
                              </span>
                            ) : (
                              <Select
                                value={line.payment_type || "one_time"}
                                onValueChange={(v) => updateLine(line._key, "payment_type", v)}
                              >
                                <SelectTrigger className="h-8 text-sm w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PAYMENT_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>
                                      {t.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>

                          {/* Priority */}
                          <TableCell>
                            {isLocked ? (
                              <span className="text-sm capitalize">{line.priority || "normal"}</span>
                            ) : (
                              <Select
                                value={line.priority || "normal"}
                                onValueChange={(v) => updateLine(line._key, "priority", v)}
                              >
                                <SelectTrigger className="h-8 text-sm w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PRIORITIES.map((p) => (
                                    <SelectItem key={p.value} value={p.value}>
                                      {p.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>

                          {/* Actions: queue toggle + remove */}
                          {!isLocked && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {canQueue && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title={
                                      isQueued
                                        ? "Remove from Expense Tracker queue"
                                        : "Add to Expense Tracker"
                                    }
                                    className={
                                      isQueued
                                        ? "h-7 w-7 text-amber-600 hover:text-amber-800 dark:text-amber-400"
                                        : "h-7 w-7 text-muted-foreground hover:text-amber-600"
                                    }
                                    onClick={() => toggleQueueLine(line._key)}
                                  >
                                    <ShoppingCart className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                  onClick={() => removeLine(line._key)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}

                    {/* Totals row */}
                    <TableRow className="font-semibold border-t-2">
                      <TableCell colSpan={3} className="text-right text-sm">
                        Totals
                      </TableCell>
                      <TableCell className="text-right text-blue-600 dark:text-blue-400">
                        {formatCurrency(totalAllocated, currency)}
                      </TableCell>
                      <TableCell className="text-right text-green-600 dark:text-green-400">
                        {formatCurrency(totalActual, currency)}
                      </TableCell>
                      {/* Payment Source column — empty in totals */}
                      <TableCell />
                      <TableCell className="text-right">
                        <span
                          className={
                            totalDiff < 0
                              ? "text-red-600 dark:text-red-400"
                              : ""
                          }
                        >
                          {formatCurrency(totalDiff, currency)}
                        </span>
                      </TableCell>
                      <TableCell />
                      {/* Empty cells for payment tracking columns: Status, Due Date, Payment Status, Payment Type, Priority */}
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      {!isLocked && <TableCell />}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {!isLocked && (
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={saving || locking}
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Saving…
                  </span>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" /> Save Draft
                  </>
                )}
              </Button>
              <Button onClick={handleSaveLock} disabled={saving || locking}>
                {locking ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Locking…
                  </span>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" /> Save &amp; Lock
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Admin delete button (shown for both draft and locked plans) */}
          {isAdmin && plan && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete Record (Admin)
              </Button>
            </div>
          )}
        </>
      )}

      {/* Add Custom Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="cat-name">Category Name</Label>
              <Input
                id="cat-name"
                placeholder="e.g. Hobbies"
                value={newCategory.category}
                onChange={(e) =>
                  setNewCategory((p) => ({ ...p, category: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-subs">
                Subcategories{" "}
                <span className="text-muted-foreground font-normal">(comma-separated)</span>
              </Label>
              <Input
                id="cat-subs"
                placeholder="e.g. Reading, Crafts, Sports"
                value={newCategory.subcategories}
                onChange={(e) =>
                  setNewCategory((p) => ({ ...p, subcategories: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory}>Add Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin delete confirmation dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Confirm Delete
            </DialogTitle>
            <DialogDescription>
              This will permanently delete this budget record and its related analytics. This
              action cannot be undone. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleAdminDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Yes, Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── History Tab ───────────────────────────────────────────────────────────────

const HistoryTab = ({ refreshSignal }) => {
  const { isAdmin } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await budgetPlannerAPI.listPlans({ status: "locked" });
      setPlans(res.data || []);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshSignal]);

  const handleAdminDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await budgetPlannerAPI.adminDeletePlan(deleteTarget.id);
      toast.success("Budget plan deleted");
      setDeleteTarget(null);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete budget plan");
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    window.location.href = exportAPI.budgetHistory();
  };

  return (
    <div className="space-y-4">
      {/* Locked / Final Status banner */}
      <div className="flex items-start gap-2 rounded-md border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400">
        <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          <strong>Locked / Final Status</strong> — These records represent final actual
          figures. Records older than 12 months will be removed; please export if needed.
        </span>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-muted-foreground">
          {plans.length} locked {plans.length === 1 ? "record" : "records"}
        </h3>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={plans.length === 0}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : plans.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No locked budget plans yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create a budget plan and click &ldquo;Save &amp; Lock&rdquo; to see it here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {plans.map((plan) => {
            const isExpanded = expandedId === plan.id;
            const totalAllocated = (plan.lines || []).reduce(
              (s, l) => s + (l.allocated_budget || 0),
              0
            );
            const totalActual = (plan.lines || []).reduce(
              (s, l) => s + (l.actual_expenditure || 0),
              0
            );
            const totalDiff = totalAllocated - totalActual;

            return (
              <Card key={plan.id} className="overflow-hidden">
                <button
                  className="w-full text-left"
                  onClick={() => setExpandedId(isExpanded ? null : plan.id)}
                >
                  <CardContent className="py-4 px-5">
                    <div className="flex flex-wrap justify-between items-center gap-3">
                      <div className="flex items-center gap-3">
                        <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-semibold">
                            {formatMonthYear(plan.month, plan.year)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Locked{" "}
                            {plan.locked_at
                              ? new Date(plan.locked_at).toLocaleDateString()
                              : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Allocated</p>
                          <p className="font-medium text-blue-600 dark:text-blue-400">
                            {formatCurrency(totalAllocated, plan.currency)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Actual</p>
                          <p className="font-medium text-green-600 dark:text-green-400">
                            {formatCurrency(totalActual, plan.currency)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Difference</p>
                          <p
                            className={
                              totalDiff < 0
                                ? "font-medium text-red-600 dark:text-red-400"
                                : "font-medium"
                            }
                          >
                            {formatCurrency(totalDiff, plan.currency)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </button>

                {isExpanded && plan.lines && plan.lines.length > 0 && (
                  <div className="px-5 pb-4 border-t">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Category</TableHead>
                          <TableHead>Subcategory</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Allocated</TableHead>
                          <TableHead className="text-right">Actual</TableHead>
                          <TableHead className="text-right">Difference</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {plan.lines.map((line, idx) => {
                          const diff =
                            (line.allocated_budget || 0) -
                            (line.actual_expenditure || 0);
                          return (
                            <TableRow key={line.id || idx}>
                              <TableCell className="text-sm">{line.category}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {line.subcategory}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {line.description}
                              </TableCell>
                              <TableCell className="text-sm text-right text-blue-600 dark:text-blue-400">
                                {formatCurrency(line.allocated_budget, plan.currency)}
                              </TableCell>
                              <TableCell className="text-sm text-right text-green-600 dark:text-green-400">
                                {formatCurrency(line.actual_expenditure, plan.currency)}
                              </TableCell>
                              <TableCell
                                className={
                                  "text-sm text-right font-medium " +
                                  (diff < 0
                                    ? "text-red-600 dark:text-red-400"
                                    : "")
                                }
                              >
                                {formatCurrency(diff, plan.currency)}
                              </TableCell>
                              <TableCell>
                                <RowStateBadge state={line.row_state || "planned"} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {isAdmin && (
                      <div className="flex justify-end pt-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setDeleteTarget(plan)}
                        >
                          <Trash2 className="h-4 w-4 mr-1.5" /> Delete Record (Admin)
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Admin delete button when collapsed */}
                {!isExpanded && isAdmin && (
                  <div className="px-5 pb-3 flex justify-end border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 mt-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(plan);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" /> Delete Record (Admin)
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Admin delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Confirm Delete
            </DialogTitle>
            <DialogDescription>
              This will permanently delete this budget record and its related analytics. This
              action cannot be undone. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleAdminDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Yes, Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Installment Schedules Tab ─────────────────────────────────────────────────

const FUNDING_SOURCE_TYPES = [
  { value: "bank_account", label: "Bank Account" },
  { value: "digital_wallet", label: "Digital Wallet" },
  { value: "credit_card", label: "Credit Card" },
  { value: "loan", label: "Loan" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];

const INSTALLMENT_STATUSES = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const PAYMENT_ITEM_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "skipped", label: "Skipped" },
];

const EMPTY_INSTALLMENT_FORM = {
  category: "",
  subcategory: "",
  description: "",
  priority: "normal",
  currency: "PHP",
  funding_source_type: "credit_card",
  funding_account_id: "",
  liability_account_id: "",
  installment_original_purchase_date: new Date().toISOString().split("T")[0],
  installment_start_date: new Date().toISOString().split("T")[0],
  installment_due_day: "15",
  installment_months: "12",
  installment_monthly_amount: "",
};

function installmentStatusBadgeClass(status) {
  if (status === "active") return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
  if (status === "completed") return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
  if (status === "paused") return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  if (status === "cancelled") return "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400";
  return "bg-gray-100 text-gray-600";
}

function paymentStatusBadgeClass(status) {
  if (status === "paid") return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
  if (status === "skipped") return "bg-gray-100 text-gray-500";
  return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
}

const InstallmentSchedulesTab = () => {
  const { user } = useAuth();
  const defaultCurrency = user?.default_currency || "PHP";

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_INSTALLMENT_FORM, currency: defaultCurrency });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [liabilityAccounts, setLiabilityAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await installmentSchedulesAPI.list();
      setSchedules(res.data || []);
    } catch { toast.error("Failed to load installment schedules"); }
    finally { setLoading(false); }
  }, []);

  const fetchSupportingData = useCallback(async () => {
    try {
      const [laRes, cashRes] = await Promise.all([
        liabilityAccountsAPI.list(),
        savingsAPI.list(),
      ]);
      setLiabilityAccounts((laRes.data || []).filter((a) => a.is_active !== false));
      setCashAccounts(cashRes.data || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchSchedules();
    fetchSupportingData();
  }, [fetchSchedules, fetchSupportingData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_INSTALLMENT_FORM, currency: defaultCurrency });
    setDialogOpen(true);
  };

  const openEdit = (schedule) => {
    setEditing(schedule);
    setForm({
      category: schedule.category || "",
      subcategory: schedule.subcategory || "",
      description: schedule.description || "",
      priority: schedule.priority || "normal",
      currency: schedule.currency || defaultCurrency,
      funding_source_type: schedule.funding_source_type || "credit_card",
      funding_account_id: schedule.funding_account_id || "",
      liability_account_id: schedule.liability_account_id || "",
      installment_original_purchase_date: schedule.installment_original_purchase_date || "",
      installment_start_date: schedule.installment_start_date || "",
      installment_due_day: String(schedule.installment_due_day || 15),
      installment_months: String(schedule.installment_months || 12),
      installment_monthly_amount: String(schedule.installment_monthly_amount || ""),
    });
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setEditing(null); };

  const isLiabilityType = (type) => type === "credit_card" || type === "loan";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category.trim()) { toast.error("Category is required"); return; }
    const monthlyAmt = parseFloat(form.installment_monthly_amount);
    if (isNaN(monthlyAmt) || monthlyAmt <= 0) { toast.error("Monthly amount must be greater than 0"); return; }
    const months = parseInt(form.installment_months, 10);
    if (isNaN(months) || months < 1 || months > 360) { toast.error("Installment months must be between 1 and 360"); return; }
    const dueDay = parseInt(form.installment_due_day, 10);
    if (isNaN(dueDay) || dueDay < 1 || dueDay > 28) { toast.error("Due day must be between 1 and 28"); return; }

    setSaving(true);
    try {
      const payload = {
        category: form.category,
        subcategory: form.subcategory || null,
        description: form.description || null,
        priority: form.priority,
        currency: form.currency,
        funding_source_type: form.funding_source_type,
        funding_account_id: form.funding_account_id || null,
        liability_account_id: isLiabilityType(form.funding_source_type) ? (form.liability_account_id || null) : null,
        installment_original_purchase_date: form.installment_original_purchase_date || null,
        installment_start_date: form.installment_start_date,
        installment_due_day: dueDay,
        installment_months: months,
        installment_monthly_amount: monthlyAmt,
      };
      if (editing) {
        await installmentSchedulesAPI.update(editing.id, payload);
        toast.success("Installment schedule updated");
      } else {
        await installmentSchedulesAPI.create(payload);
        toast.success("Installment schedule created");
      }
      closeDialog();
      fetchSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save installment schedule");
    } finally { setSaving(false); }
  };

  const handleDelete = async (schedule) => {
    try {
      await installmentSchedulesAPI.delete(schedule.id);
      toast.success("Installment schedule deleted");
      setDeleteConfirm(null);
      fetchSchedules();
    } catch { toast.error("Failed to delete installment schedule"); }
  };

  const handleMarkPayment = async (scheduleId, month, status) => {
    try {
      await installmentSchedulesAPI.markPayment(scheduleId, month, { status });
      fetchSchedules();
      toast.success(`Payment marked as ${status}`);
    } catch { toast.error("Failed to update payment status"); }
  };

  const liabilityNameMap = React.useMemo(
    () => Object.fromEntries(liabilityAccounts.map((a) => [a.id, a.name])),
    [liabilityAccounts]
  );

  const cashAccountNameMap = React.useMemo(
    () => Object.fromEntries(cashAccounts.map((a) => [a.id, a.account_label || a.bank_name])),
    [cashAccounts]
  );

  const getLiabilityName = (id) => liabilityNameMap[id] ?? id;
  const getCashAccountName = (id) => cashAccountNameMap[id] ?? id;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Installment Schedules</h2>
          <p className="text-sm text-muted-foreground">Track purchases paid in monthly installments across credit cards and loans.</p>
        </div>
        <Button onClick={openCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />New Schedule
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((n) => (
            <Card key={n} className="animate-pulse"><CardContent className="p-4 space-y-3">
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-3 bg-muted rounded w-2/3" />
            </CardContent></Card>
          ))}
        </div>
      ) : schedules.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <CreditCard className="h-12 w-12 text-muted-foreground/40" />
            <div>
              <p className="text-base font-medium text-muted-foreground">No installment schedules yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Track purchases paid in installments — credit cards, loans, or any split-payment plan.
              </p>
            </div>
            <Button onClick={openCreate} size="sm"><Plus className="h-4 w-4 mr-2" />Add Your First Schedule</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {schedules.map((schedule) => {
            const isExpanded = expandedId === schedule.id;
            const paidCount = (schedule.schedule || []).filter((s) => s.status === "paid").length;
            const totalMonths = schedule.installment_months || 0;
            const progressPct = totalMonths > 0 ? Math.round((paidCount / totalMonths) * 100) : 0;
            const liabilityName = schedule.liability_account_id ? getLiabilityName(schedule.liability_account_id) : null;

            return (
              <Card key={schedule.id} className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{schedule.description || schedule.category}</p>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${installmentStatusBadgeClass(schedule.installment_status)}`}>
                          {schedule.installment_status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{schedule.category}{schedule.subcategory ? ` › ${schedule.subcategory}` : ""}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                        <span className="text-muted-foreground">
                          {formatCurrency(schedule.installment_monthly_amount, schedule.currency)}/mo × {schedule.installment_months}
                        </span>
                        <span className="text-muted-foreground">
                          Remaining: <strong className="text-foreground">{formatCurrency(schedule.installment_remaining_balance, schedule.currency)}</strong>
                        </span>
                        {liabilityName && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <CreditCard className="h-3 w-3" />
                            <span>{liabilityName}</span>
                          </span>
                        )}
                      </div>
                      {/* Progress bar */}
                      <div className="mt-3 space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{paidCount} of {totalMonths} paid</span>
                          <span>{progressPct}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(schedule)} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteConfirm(schedule)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setExpandedId(isExpanded ? null : schedule.id)}
                        title={isExpanded ? "Collapse" : "Expand payments"}
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Payment schedule */}
                  {isExpanded && (schedule.schedule || []).length > 0 && (
                    <div className="mt-4 border-t border-border pt-4">
                      <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Payment Schedule</p>
                      <div className="space-y-2">
                        {schedule.schedule.map((item) => (
                          <div key={item.month} className="flex items-center justify-between gap-2 text-sm">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-muted-foreground w-8 text-right shrink-0">#{item.month}</span>
                              <span className="text-muted-foreground shrink-0">{item.due_date}</span>
                              <span className="font-medium">{formatCurrency(item.amount, schedule.currency)}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentStatusBadgeClass(item.status)}`}>
                                {item.status}
                              </span>
                              {item.status !== "paid" && schedule.installment_status !== "cancelled" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-green-600 hover:text-green-700"
                                  onClick={() => handleMarkPayment(schedule.id, item.month, "paid")}
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Mark Paid
                                </Button>
                              )}
                              {item.status === "paid" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-muted-foreground"
                                  onClick={() => handleMarkPayment(schedule.id, item.month, "pending")}
                                >
                                  Undo
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {editing ? "Edit Installment Schedule" : "New Installment Schedule"}
            </DialogTitle>
            <DialogDescription>
              Track a purchase paid in monthly installments. Cash is not deducted until each payment is marked as paid.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Category */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category <span className="text-destructive">*</span></Label>
                <Input
                  placeholder="e.g. Electronics"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Subcategory</Label>
                <Input
                  placeholder="e.g. Laptop"
                  value={form.subcategory}
                  onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                placeholder="e.g. MacBook Pro M3 — BPI 12-month installment"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>

            {/* Funding */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Funding Source Type</Label>
                <Select value={form.funding_source_type} onValueChange={(v) => setForm((f) => ({ ...f, funding_source_type: v, liability_account_id: "" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FUNDING_SOURCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["PHP", "USD", "EUR", "GBP", "SGD", "AUD"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Liability Account — shown only for credit_card or loan */}
            {isLiabilityType(form.funding_source_type) && (
              <div className="space-y-1.5 rounded-md border border-border p-3 bg-muted/30">
                <Label className="flex items-center gap-1.5">
                  <CreditCard className="h-3.5 w-3.5 text-primary" />
                  Liability Account
                  <span className="text-xs font-normal text-muted-foreground ml-1">(optional — tracks which card/loan carries the obligation)</span>
                </Label>
                {liabilityAccounts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No liability accounts configured. Add one in <strong>Accounts &amp; Balances → Liability Accounts</strong>.
                  </p>
                ) : (
                  <Select
                    value={form.liability_account_id || "__none__"}
                    onValueChange={(v) => setForm((f) => ({ ...f, liability_account_id: v === "__none__" ? "" : v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select a card or loan (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {(() => {
                        const filtered = liabilityAccounts.filter((a) => a.type === form.funding_source_type);
                        if (filtered.length === 0) {
                          return (
                            <SelectItem value="__no_match__" disabled>
                              No {form.funding_source_type === "credit_card" ? "credit cards" : "loans"} configured
                            </SelectItem>
                          );
                        }
                        return filtered.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.name}{a.institution ? ` — ${a.institution}` : ""}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Cash account used for payment (optional) */}
            {cashAccounts.length > 0 && (
              <div className="space-y-1.5">
                <Label>Paying From Account <span className="text-xs font-normal text-muted-foreground">(cash account used to settle the bill — optional)</span></Label>
                <Select
                  value={form.funding_account_id || "__none__"}
                  onValueChange={(v) => setForm((f) => ({ ...f, funding_account_id: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select cash account (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {cashAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.account_label || a.bank_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Purchase Date <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
                <Input
                  type="date"
                  value={form.installment_original_purchase_date}
                  onChange={(e) => setForm((f) => ({ ...f, installment_original_purchase_date: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>First Payment Date <span className="text-destructive">*</span></Label>
                <Input
                  type="date"
                  value={form.installment_start_date}
                  onChange={(e) => setForm((f) => ({ ...f, installment_start_date: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* Installment terms */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Monthly Amount <span className="text-destructive">*</span></Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={form.installment_monthly_amount}
                  onChange={(e) => setForm((f) => ({ ...f, installment_monthly_amount: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Months <span className="text-destructive">*</span></Label>
                <Input
                  type="number"
                  min="1"
                  max="360"
                  placeholder="12"
                  value={form.installment_months}
                  onChange={(e) => setForm((f) => ({ ...f, installment_months: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Due Day <span className="text-destructive">*</span></Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  placeholder="15"
                  value={form.installment_due_day}
                  onChange={(e) => setForm((f) => ({ ...f, installment_due_day: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* Total preview */}
            {form.installment_monthly_amount && form.installment_months && (
              <div className="rounded-md bg-muted/40 px-3 py-2 text-sm flex justify-between">
                <span className="text-muted-foreground">Total Commitment</span>
                <strong>{formatCurrency(parseFloat(form.installment_monthly_amount || 0) * parseInt(form.installment_months || 0, 10), form.currency)}</strong>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeDialog} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? "Saving..." : editing ? "Save Changes" : "Create Schedule"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertCircle className="h-5 w-5" />Delete Schedule?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Delete <strong>{deleteConfirm?.description || deleteConfirm?.category}</strong>? This cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function BudgetPlannerPage() {
  const [activeTab, setActiveTab] = useState("form");
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const handlePlanLocked = () => {
    setHistoryRefresh((n) => n + 1);
    setActiveTab("history");
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Budget Planner</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Plan, track, and review your monthly budget.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="form">Budget Form</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="installments" className="flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" />Installment Schedules
          </TabsTrigger>
        </TabsList>

        <TabsContent value="form">
          <BudgetFormTab onPlanLocked={handlePlanLocked} />
        </TabsContent>

        <TabsContent value="history">
          <HistoryTab refreshSignal={historyRefresh} />
        </TabsContent>

        <TabsContent value="installments">
          <InstallmentSchedulesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
